<template>
  <form :class="{ 'form': true }">
    <svg class="logo">
      <use xlink:href="#bookero-logo" />
    </svg>

    <div class="wrapper">
      <h1>{{ $t('passwordReminder.title') }}</h1>

      <hr>

      <transition-group name="slide-down">
        <message
          v-if="emailIsEmpty"
          type="error"
          key="msg-1">
          {{ $t('passwordReminder.emailIsRequired') }}
        </message>

        <message
          v-if="connectionError"
          type="error"
          key="msg-2">
          {{ $t('passwordReminder.anErrorOccurred') }}
        </message>

        <message
          v-if="emailSent"
          type="success"
          key="msg-3">
          {{ $t('passwordReminder.ifEmailExistLinkSent') }}
        </message>

        <message
          v-if="emailIsInvalid"
          type="error"
          key="msg-4">
          {{ $t('passwordReminder.invalidEmail') }}
        </message>
      </transition-group>

      <label v-if="!emailSent">
        {{ $t('passwordReminder.provideEmailConnectedWithAccount') }}
        <input
          type="email"
          v-model="email"
          @keyup="removeEmpty('email')"
          @focus="removeError('email')"
          tabindex="1"
          :class="{ 'is-invalid': emailIsInvalid || emailIsEmpty }"
          :placeholder="$t('passwordReminder.yourEmail')" />
      </label>

      <btn
        v-if="!emailSent"
        type="success wide"
        @click.native="resetPassword"
        :disabled="formSent"
        :tabindex="3">
        <template v-if="!formSent">{{ $t('passwordReminder.reset') }}</template>
        <template v-if="formSent">{{ $t('passwordReminder.resetting') }}</template>
      </btn>

      <div
        class="footer">
        <span>{{ $t('passwordReminder.wantToLogin') }}</span>
        <router-link
          to="/login"
          tabindex="4">
          {{ $t('passwordReminder.backToLogin') }}
        </router-link>
      </div>
    </div>

    <btn
      v-if="$root.appLanguage === 'pl'"
      type="with-icon icon-larger"
      title="Switch to english"
      iconName="en"
      iconColor="color-text-light"
      @click.native.prevent="setLang('en')"
      class="switch-lang">
      Switch to english
    </btn>

    <btn
      v-if="$root.appLanguage === 'en'"
      type="with-icon icon-larger"
      title="Zmień na polski"
      iconName="pl"
      iconColor="color-text-light"
      @click.native.prevent="setLang('pl')"
      class="switch-lang">
      Zmień na polski
    </btn>
  </form>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'password-reminder',
  data () {
    return {
      email: '',
      emailSent: false,
      formSent: false,
      emailIsInvalid: false,
      emailIsEmpty: false,
      connectionError: false
    };
  },
  mounted () {
    localStorage.removeItem('bookero-auth-token');
    Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer null';
    this.$store.commit('config/clearConfig');
    this.$store.commit('global/clear');

    if (typeof window.bugsnagClient !== 'undefined') {
      window.bugsnagClient.setUser('0');
    }

    if (typeof window.ga !== 'undefined') {
      window.ga('set', 'dimension1', 0);
    }
  },
  methods: {
    resetPassword (e) {
      e.preventDefault();
      this.connectionError = false;

      if (!this.validate()) {
        return;
      }

      this.formSent = true;

      this.$http.post('/password/email', {
        'email': this.email
      }).then(response => {
        this.formSent = false;
        this.emailSent = true;
      }).catch(error => {
        if (error.response.status === 422) {
          this.emailIsInvalid = true;
        }

        if (!error.response) {
          this.connectionError = true;

          setTimeout(() => {
            this.connectionError = false;
          }, 3000);
        }

        this.formSent = false;
      });
    },
    validate () {
      let formIsValid = true;

      if (this.email.trim() === '') {
        this.emailIsEmpty = true;
        formIsValid = false;
      }

      return formIsValid;
    },
    removeError (field) {
      if (field === 'email') {
        this.emailIsInvalid = false;
      }
    },
    removeEmpty (field) {
      if (field === 'email') {
        this.emailIsEmpty = false;
      }
    },
    setLang (newLang) {
      this.$bus.$emit('bookero-set-language', newLang);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.form {
  left: 50%;
  margin: 0;
  max-width: 94%;
  padding: 3.2rem 0;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  transition: opacity .5s ease-out;
  width: 41rem;
  will-change: opacity;

  .logo {
    display: block;
    height: 3.6rem;
    margin: 0 auto 3.2rem auto;
  }

  .wrapper {
    background: $color-bg-lightest;
    border-radius: $border-radius-small;
    padding: 3rem;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      margin: 0 0 3rem 0;
      text-align: center;
    }

    hr {
      border: none;
      border-top: 1px solid $color-border;
      margin-bottom: 3rem;
    }

    label {
      display: block;
      font-weight: 400;
      position: relative;

      input {
        background: $color-bg-lightest;
        border: 1px solid $color-border;
        border-radius: $border-radius-small;
        font-size: 1.4rem;
        margin: .5rem 0 1rem 0;
        padding: 1rem;
        width: 100%;

        &::-webkit-input-placeholder {
          color: $color-text-lightest;
        }

        &::-moz-placeholder {
          color: $color-text-lightest;
        }

        &:-ms-input-placeholder {
          color: $color-text-lightest;
        }

        &:-moz-placeholder {
          color: $color-text-lightest;
        }

        &.is-invalid {
          border-color: $color-danger;
          color: $color-danger;
        }
      }

      a {
        font-size: 1.4rem;
        position: absolute;
        right: 0;
        top: .25rem;
      }
    }

    & > .button {
      margin: 1.5rem auto;
    }
  }

  .footer {
    background: $color-bg-medium;
    border-radius: 0 0 $border-radius-small $border-radius-small;
    margin: 3rem -3rem -3rem -3rem;
    padding: 2rem 3rem;
    text-align: center;

    a,
    span {
      display: block;
      line-height: 1.5;
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all .25s ease-out;
}

.slide-down-enter, .slide-down-leave-to {
  height: 0;
  opacity: 0;
}

@media (max-width: 480px) {
  .form {
    .wrapper {
      padding: 2rem;
    }

    .footer {
      margin: 3rem -2rem -2rem -2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/PasswordReminder.vue